/**
 * The palette, taken from the retail billing suite so the two apps look like one
 * product. Light and dark are defined side by side: every token that exists in one
 * exists in the other, which is what stops dark mode falling back to a light value
 * on some stray surface.
 */

export type ThemeMode = 'light' | 'dark';

export interface AppColors {
  primary: { main: string; dark: string; light: string; contrastText: string };
  secondary: { main: string; dark: string; light: string; contrastText: string };
  error: { main: string; dark: string; light: string };
  warning: { main: string; dark: string; light: string };
  info: { main: string; dark: string; light: string };
  success: { main: string; dark: string; light: string };
  text: { primary: string; secondary: string; disabled: string };
  background: { default: string; paper: string; raised: string };
  divider: string;
  /** The app bar, which is dark in both modes - as it is in the retail app. */
  appBar: { background: string; text: string; activeText: string };
  /** The keyboard focus ring used across billing. */
  focusRing: string;
}

const light: AppColors = {
  primary: { main: '#196ae5', dark: '#0f4089', light: '#4788ea', contrastText: '#ffffff' },
  secondary: { main: '#ff9800', dark: '#ef6c00', light: '#ffb74d', contrastText: 'rgba(0, 0, 0, 0.87)' },
  error: { main: '#f33333', dark: '#d10b25', light: '#f9494f' },
  warning: { main: '#ff9800', dark: '#ef6c00', light: '#ffb74d' },
  info: { main: '#196ae5', dark: '#0f4089', light: '#4788ea' },
  success: { main: '#22bb34', dark: '#00880f', light: '#74d176' },
  text: {
    primary: 'rgba(33, 33, 33, 0.92)',
    secondary: 'rgba(33, 33, 33, 0.72)',
    disabled: 'rgba(33, 33, 33, 0.38)',
  },
  background: { default: '#fafafa', paper: '#ffffff', raised: '#ffffff' },
  divider: 'rgba(33, 33, 33, 0.12)',
  appBar: { background: '#001529', text: 'rgba(255, 255, 255, 0.72)', activeText: '#ffffff' },
  focusRing: '#66bbff',
};

const dark: AppColors = {
  primary: { main: '#66bbff', dark: '#3a9ae8', light: '#e8f0fc', contrastText: 'rgba(0, 0, 0, 0.87)' },
  secondary: { main: '#ffcc80', dark: '#ffb74d', light: '#ffe0b2', contrastText: 'rgba(0, 0, 0, 0.87)' },
  error: { main: '#f44336', dark: '#d32f2f', light: '#e57373' },
  warning: { main: '#ffcc80', dark: '#ffb74d', light: '#ffe0b2' },
  info: { main: '#29b6f6', dark: '#0288d1', light: '#4fc3f7' },
  success: { main: '#66bb6a', dark: '#388e3c', light: '#81c784' },
  text: {
    primary: '#ffffff',
    secondary: 'rgba(255, 255, 255, 0.7)',
    disabled: 'rgba(255, 255, 255, 0.5)',
  },
  background: { default: '#121212', paper: '#1e1e1e', raised: '#252525' },
  divider: 'rgba(255, 255, 255, 0.12)',
  appBar: { background: '#0a0f16', text: 'rgba(255, 255, 255, 0.72)', activeText: '#ffffff' },
  focusRing: '#66bbff',
};

export const getColors = (mode: ThemeMode): AppColors => (mode === 'dark' ? dark : light);
